import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ColorSwatch from "@/components/ColorSwatch";

export default function VehiclesCarousel() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get("/api/vehicles"),
      axios.get("/api/categorias?visible_only=true"),
    ]).then(([rv, rc]) => {
      const visibleCats = new Set((rc.data || []).map(c => c.nombre));
      const filtered = (rv.data || []).filter(v => v.visible && visibleCats.has(v.categoria));
      setVehicles(filtered);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (vehicles.length === 0) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % vehicles.length), 4500);
    return () => clearInterval(timerRef.current);
  }, [vehicles]);

  if (vehicles.length === 0) return null;
  const perView = 3;
  const visible = [];
  for (let i = 0; i < Math.min(perView, vehicles.length); i++) {
    visible.push(vehicles[(idx + i) % vehicles.length]);
  }
  const prev = () => setIdx(i => (i - 1 + vehicles.length) % vehicles.length);
  const next = () => setIdx(i => (i + 1) % vehicles.length);

  return (
    <section className="section-light py-16" data-testid="vehicles-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="divider-red mb-4"></div>
            <h2 className="font-display text-4xl sm:text-5xl">{t("vehicles.titulo")}</h2>
            <p className="text-neutral-600 mt-2">{t("vehicles.cochesDisponiblesAhora")}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={prev} className="p-2 border border-neutral-300 rounded hover:border-[var(--pc-red)]" data-testid="carousel-prev"><ChevronLeft /></button>
            <button onClick={next} className="p-2 border border-neutral-300 rounded hover:border-[var(--pc-red)]" data-testid="carousel-next"><ChevronRight /></button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map(v => (
            <div key={v.id} className="bg-white rounded-lg overflow-hidden border border-neutral-200 hover:border-[var(--pc-red)] transition-colors" data-testid={`carousel-card-${v.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img src={v.imagen_url} alt={v.marca_modelo} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <ColorSwatch color={v.color} size={12} />
                  <h3 className="font-display text-xl text-black truncate">{v.marca_modelo}</h3>
                </div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">{v.categoria} · {v.transmision}{v.color ? ` · ${v.color}` : ""}</div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-display text-[var(--pc-red)]">{(v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? "—" : `${Number(v.precio_dia).toFixed(2)} €`}</div>
                    <div className="text-xs text-neutral-500">{t("vehicles.porDia")}</div>
                  </div>
                  <Link to={`/vehiculo/${v.id}`} className="btn-red px-4 py-2 text-sm" data-testid={`carousel-details-${v.id}`}>{t("vehicles.verDetalles")}</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
